import { findEventEntry, lookup } from "@repo/vihangam-yog-shared";
import { useEffect, useState } from "react";

import { API_BASE } from "../../TrpcProvider";
import { track } from "../../analytics";
import { OCTOBER } from "../../content/october";
import { Page } from "../../layout/Page";
import { trpc } from "../../trpc";
import { Agenda } from "../../ui/Agenda";
import { Button } from "../../ui/Button";
import { Callout } from "../../ui/Callout";
import { EventWhere } from "../../ui/EventWhere";
import { Section } from "../../ui/Section";

import { isLastHawan, lastHawanLabel } from "./lastHawan";
import { clearDraft } from "./useBundleDraft";

const POLL_MS = 3000;
const POLL_LIMIT = 7;

const NEED_TAGS = {
  wheelchair: "Wheelchair",
  chair: "Chair",
} as const;

/** One letter after the age, as the organisers' seating sheet has it. */
const GENDER_MARKS = {
  female: "F",
  male: "M",
  other: "O",
} as const;

/** "(34)", "(64, F)", "(2, wheelchair)": the facts the organisers plan around. */
function memberDetail(p: {
  kind: "adult" | "child";
  age?: number;
  gender?: keyof typeof GENDER_MARKS;
  needs?: readonly ("wheelchair" | "chair")[];
}): string {
  const parts: string[] = [p.age === undefined ? p.kind : `${p.age}`];
  if (p.gender) {
    parts.push(GENDER_MARKS[p.gender]);
  }
  for (const need of p.needs ?? []) {
    parts.push(NEED_TAGS[need].toLowerCase());
  }
  return `(${parts.join(", ")})`;
}

/** The short title the form used for a Hawan option, with the units
 *  bought when more than one: "Your own Kund × 2", "Shared Kund × 3". */
const OPTION_TITLES = {
  shared: "Shared Kund",
  kund: "Your own Kund",
} satisfies Record<string, string>;

function optionLine(tier: {
  id: string;
  label: string;
  quantity: number;
}): string {
  const title = lookup(OPTION_TITLES, tier.id) ?? tier.label;
  return tier.quantity > 1 ? `${title} × ${tier.quantity}` : title;
}

/** The practical notices for a ticket's day; none for a slug outside the
 *  two October days. */
function dayNotices(eventSlug: string): string[] {
  if (eventSlug === OCTOBER.hawan.slug) {
    return OCTOBER.hawan.notices;
  }
  if (eventSlug === OCTOBER.stage.slug) {
    return OCTOBER.stage.notices;
  }
  return [];
}

/** "Last Hawan: Never" for a ticket that carries the answer; null when the
 *  ticket has none or the server does not send the field yet. Generic so
 *  it reads the field off whatever ticket shape the router publishes. */
function lastHawanLine<T extends { eventSlug: string }>(
  ticket: T,
): string | null {
  if (!("lastHawan" in ticket) || !isLastHawan(ticket.lastHawan)) {
    return null;
  }
  return `Last Hawan: ${lastHawanLabel(ticket.lastHawan)}`;
}

function expectHawan(): boolean {
  try {
    return sessionStorage.getItem("vysg-register-expect-hawan") === "1";
  } catch {
    return false;
  }
}

export function ThanksPage() {
  const ref = new URLSearchParams(window.location.search).get("ref") ?? "";
  const [polls, setPolls] = useState(0);
  const query = trpc.bundleByRef.useQuery(
    { confirmationRef: ref },
    { enabled: ref.length > 0 },
  );
  const tickets = query.data?.tickets ?? [];
  const hasHawan = tickets.some((t) => t.eventSlug === OCTOBER.hawan.slug);
  const waiting = expectHawan() && !hasHawan && polls < POLL_LIMIT;

  // A registration is through: the saved draft has done its job.
  useEffect(() => {
    clearDraft();
  }, []);

  useEffect(() => {
    track("thanks_viewed", { ref_present: ref.length > 0 });
  }, [ref]);
  useEffect(() => {
    if (!waiting) {
      if (hasHawan) {
        try {
          sessionStorage.removeItem("vysg-register-expect-hawan");
        } catch {
          /* ignore */
        }
      }
      return;
    }
    const t = setTimeout(() => {
      void query.refetch();
      setPolls((n) => n + 1);
    }, POLL_MS);
    return () => clearTimeout(t);
  }, [waiting, hasHawan, polls, query]);

  const party = tickets.find((t) => t.party.length > 0)?.party ?? [];
  const note =
    tickets.map((t) => t.registrantNote?.trim() ?? "").find(Boolean) ?? "";
  const groupName =
    tickets.map((t) => t.groupName?.trim() ?? "").find(Boolean) ?? "";
  return (
    <Page title="Registered" heading="You're registered">
      <Section eyebrow="Thank you">
        {!ref ? (
          <p>
            We couldn't find a reference in this link. Check the email we sent
            you.
          </p>
        ) : null}
        {ref ? (
          <div className="max-w-2xl">
            <p className="text-ink-soft">Your reference</p>
            <p className="font-mono text-2xl tracking-wider text-ink md:text-3xl">
              {ref}
            </p>
            <ul className="mt-6 grid gap-4">
              {tickets.map((t) => {
                const e = findEventEntry(t.eventSlug);
                const notices = dayNotices(t.eventSlug);
                const lastHawan =
                  t.eventSlug === OCTOBER.hawan.slug ? lastHawanLine(t) : null;
                return (
                  <li
                    key={t.eventSlug}
                    className="rounded-2xl border border-hairline bg-card p-5 md:p-6"
                  >
                    {/* Same split as DayCard so a booking looks like the
                        same object before and after submission. */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                      <div className="min-w-0">
                        <p className="eyebrow">{e?.dateLabel ?? t.eventSlug}</p>
                        <p className="font-display mt-1.5 text-xl text-ink">
                          {e?.title ?? t.eventSlug}
                        </p>
                        {e?.timeLabel ? (
                          <p className="mt-1 text-sm text-ink">{e.timeLabel}</p>
                        ) : null}
                        {e ? <EventWhere event={e} className="mt-1" /> : null}
                      </div>
                      <div className="shrink-0 text-sm sm:text-right">
                        <p className="font-medium text-teal-deep">
                          {t.bookingType === "paid" ? "Paid" : "Free entry"}
                          {t.tier ? ` · ${optionLine(t.tier)}` : ""}
                          {t.foodPreference
                            ? ` · ${t.foodPreference === "jain" ? "Jain" : "Vegetarian"} food`
                            : ""}
                        </p>
                        {lastHawan ? (
                          <p className="mt-0.5 text-ink-soft">{lastHawan}</p>
                        ) : null}
                      </div>
                    </div>
                    {e?.arriveByLabel ||
                    t.eventSlug === OCTOBER.hawan.slug ||
                    notices.length > 0 ? (
                      <Callout className="mt-4">
                        {e?.arriveByLabel ? (
                          <p className="font-semibold">
                            Arrive by {e.arriveByLabel}
                          </p>
                        ) : null}
                        {t.eventSlug === OCTOBER.hawan.slug ? (
                          <p>
                            Your Kund number will be shared closer to the day by
                            email and WhatsApp.
                          </p>
                        ) : null}
                        {notices.map((line) => (
                          <p key={line}>{line}</p>
                        ))}
                      </Callout>
                    ) : null}
                    <Agenda rows={e?.agenda} className="mt-4" />
                  </li>
                );
              })}
            </ul>
            {waiting ? (
              <p className="mt-4 text-sm text-ink-soft" role="status">
                Confirming your payment with Stripe. This usually takes a few
                seconds.
              </p>
            ) : null}
            {expectHawan() && !hasHawan && !waiting ? (
              <p className="mt-4 text-sm text-ink-soft" role="status">
                Your payment is being confirmed. The confirmation email will
                follow; you don't need to do anything else.
              </p>
            ) : null}
            {groupName ? (
              <div className="mt-6">
                <p className="text-sm font-semibold text-ink">Coming with</p>
                <p className="mt-1 text-sm text-ink-soft">{groupName}</p>
              </div>
            ) : null}
            {party.length > 0 ? (
              <div className="mt-6">
                <p className="text-sm font-semibold text-ink">Coming</p>
                <ul className="mt-1 flex flex-wrap gap-2">
                  {party.map((p, i) => (
                    <li
                      key={`${p.name}-${i}`}
                      className="rounded-full bg-ivory-deep px-3 py-1 text-sm text-ink"
                    >
                      {p.name}{" "}
                      <span className="text-ink-soft">{memberDetail(p)}</span>
                    </li>
                  ))}
                </ul>
                {/* One code per person: the desk scans whoever is at the
                    front, and the rest of the family need not be. */}
                <p className="mt-4 text-sm text-ink-soft">
                  Each person shows their own code at the door, or just give
                  your name.
                </p>
                <ul className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                  {party.map((p, i) => (
                    <li
                      key={`pass-${p.name}-${i}`}
                      className="flex flex-col items-center gap-1.5 rounded-2xl border border-hairline bg-card p-3"
                    >
                      <img
                        src={`${API_BASE}/qr/${encodeURIComponent(ref)}/${i + 1}.png`}
                        alt={`Pass for ${p.name}`}
                        width={120}
                        height={120}
                        loading="lazy"
                        className="h-[120px] w-[120px] rounded-lg bg-white"
                      />
                      <span className="text-center text-sm text-ink">
                        {p.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {note ? (
              <div className="mt-6">
                <p className="text-sm font-semibold text-ink">You told us</p>
                <p className="mt-1 max-w-prose text-sm whitespace-pre-line text-ink-soft">
                  {note}
                </p>
              </div>
            ) : null}
            <p className="mt-6 text-sm text-ink-soft">
              We've emailed this to you. Bring the reference on the day, or just
              give us your name.
            </p>
            <div className="mt-6 flex gap-3">
              <Button href="/">Home</Button>
              <Button href="/events" variant="secondary">
                Events
              </Button>
            </div>
          </div>
        ) : null}
      </Section>
    </Page>
  );
}
